import * as React from 'react';
import './additionalMandatoryReviewer.scss';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { NodeType } from '../../../enums/NodeType';
import { getSchema } from '../../../utils';
import { Metadata } from '../../../types';
import { useSelector } from '../../../utils/useSelector';
import { useLoader } from '../../Loader/loader';
import { useAlert } from '../../Alert/alert';
import { ReferenceSearch } from '../../ReferenceSearch/referenceSearch';
import { Dropdown } from '../../Dropdown/dropdown';
import { DownArrow } from '../../Icon/icon';
import { Close } from 'rcd-cpp-ui/ui/icon/Remove';
import { ApprovalReason } from '../../../utils/constants';

type Props = {
  data: Partial<Metadata>;
  onSave: (selectedReviewers: Metadata[]) => Promise<any>;
  onClose: () => any;
};

type Option = {
  reasonId: number;
  displayText: string;
};

type ReasonOption = { reasonId: number; displayText: string };

const ReasonOptions = [
  {
    reasonId: ApprovalReason.Additional_KI_Leader,
    displayText: 'Additional KI Leader',
    active: true
  },
  {
    reasonId: ApprovalReason.Contextualization_Root_Author,
    displayText: 'Contextualization Root Author',
    active: true
  },
  {
    reasonId: ApprovalReason.Primary_Research,
    displayText: 'Primary Research',
    active: true
  },
  {
    reasonId: ApprovalReason.Secondary_Research,
    displayText: 'Secondary Research',
    active: true
  },
  {
    reasonId: ApprovalReason.Subject_Matter_Expert,
    displayText: 'Subject Matter Expert',
    active: true
  },
  {
    reasonId: ApprovalReason.Vendor_Lead,
    displayText: 'Vendor Lead',
    active: true
  },
  { reasonId: ApprovalReason.Other, displayText: 'Other', active: true },
  {
    reasonId: ApprovalReason.DisrupterReviewer,
    displayText: 'Disrupter Reviewer',
    active: false
  }
];

export const ReviewerModal = (p: Props) => {
  const { data, onClose } = p;
  const getAPI = useSelector(s => s.app?.elFn?.getAPI);
  const itemSchema = useSelector(s =>
    getSchema(s.planning.selectedWorkspace?.schemas, NodeType.Item)
  );
  const [selectedReviewers, setSelectedReviewers] = React.useState<Metadata[]>(
    !!data?.props?.additionalMandatoryReviewers?.length
      ? data.props.additionalMandatoryReviewers
      : []
  );
  const [pendingReviewer, setPendingReviewer] = React.useState<Metadata>(null);
  const [pendingReason, setPendingReason] = React.useState<ReasonOption>(null);
  const actions = useAlert({ onSave: p.onSave });
  const { isLoading, Loader, onSave } = useLoader(useAlert(actions));

  const { api } = React.useMemo(() => {
    return { api: getAPI() };
  }, []);

  return (
    <div className="slider-activator">
      <Modal
        id="additionalMandatoryReviewerModal"
        onClose={onClose}
        header={`${
          !!data?.props?.additionalMandatoryReviewers?.length ? 'Edit' : 'Add'
        } Reviewers`}
        footer={
          <Buttons>
            <Secondary label="Cancel" onClick={onClose} />
            <Primary
              label="Save"
              onClick={async () => {
                await onSave(selectedReviewers);
                onClose();
              }}
              disabled={isLoading}
            />
          </Buttons>
        }
      >
        <div className="input-container">
          <ReferenceSearch
            onChange={values => setPendingReviewer(values[0])}
            api={api}
            seeOptions={null}
            nodeType={NodeType.Employee}
            multiple={false}
            maxResults={10}
            minLength={0}
            selected={!!pendingReviewer ? [pendingReviewer] : []}
            includeIds={null}
            getClassName={o => {
              return `fs-mask`;
            }}
            shouldMask={true}
            schemaField={null}
            placeholder="Additional Mandatory Reviewers"
          />
          <Dropdown
            className="reason-dd"
            options={ReasonOptions?.filter(ro => ro.active)}
            position="right"
            onSelect={o => setPendingReason(o)}
            renderOption={(o: Option) => {
              return (
                <div>
                  <div className="dd-text" data-txt={o.displayText}>
                    {o.displayText}
                  </div>
                </div>
              );
            }}
          >
            <div className="dd-field-input">
              <div className="dd-field-text">
                {!pendingReason ? `Reason` : pendingReason.displayText}
              </div>
              <div>
                <DownArrow />
              </div>
            </div>
          </Dropdown>
          <Primary
            disabled={!pendingReviewer || !pendingReason || isLoading}
            onClick={() => {
              const reviewerToAdd = {
                ...pendingReviewer,
                relationship: {
                  props: {
                    field: 'additionalMandatoryReviewers',
                    reasonId: pendingReason.reasonId
                  },
                  type: `ITEM_APPROVER`
                }
              };
              if (
                !selectedReviewers.find(
                  r => r.props.refId == reviewerToAdd.props.refId
                )
              ) {
                setSelectedReviewers([...selectedReviewers, reviewerToAdd]);
              } else {
                setSelectedReviewers(
                  selectedReviewers.map(r => {
                    if (r.props.refId == reviewerToAdd.props.refId) {
                      return reviewerToAdd;
                    } else {
                      return r;
                    }
                  })
                );
              }

              setPendingReviewer(null);
              setPendingReason(null);
            }}
          >
            Add
          </Primary>
        </div>
        <Loader />
        <div className="selected-reviewer-list-container">
          <div className="selected-reviewer-list-header">
            <div className="name-label">Reviewer Name</div>{' '}
            <div className="reason-label">Reason</div>
            <div></div>
          </div>
          {!!selectedReviewers.length &&
            selectedReviewers.map((r, i) => {
              return (
                <div className="selected-reviewer-container" key={i}>
                  <div className="name">{r.props.name}</div>
                  <div className="reason">
                    {getReasonText(r.relationship?.props.reasonId)}
                  </div>
                  <div className="selected-reviewer-remove-container">
                    <div
                      className="remove-reviewer"
                      onClick={() => {
                        setSelectedReviewers([
                          ...selectedReviewers.filter((r, index) => index !== i)
                        ]);
                      }}
                    >
                      <Close />
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </Modal>
    </div>
  );
};

export const getReasonText = (reasonId: number) => {
  const reasonMatch = ReasonOptions.filter(ro => ro.reasonId == reasonId);
  return !!reasonMatch?.length ? reasonMatch[0]?.displayText : '--';
};
